import React, { useCallback, useEffect, useRef, useState } from "react";
import { formatTime } from "../common/common";
import { TARGET_BASIS_COST, searchTargetOf } from "../../pricingRequest";
import "./RecentSearches.css";

// Recent searches (plan ppe-ui-option-b, UI-3): one list, two places. On the
// first visit it sits under the search form; after a search the clock icon in
// the page header opens the same list. Picking a row reruns that search at
// once, as the old card and drawer did.

const HISTORY_URL = "/los/priceengine/getsearchhistory?source=ppe";
const FIRST_VISIT_ROWS = 8;
const THIRTY_DAYS = 30 * 24 * 60 * 60 * 1000;

// The stored form body, brought to what the rerun expects. Same two fixes the
// old card (topSearches.js) and drawer (rightDrawer.js) made, so the request a
// rerun sends is unchanged.
export function rerunParameters(parameters) {
  const out = {
    ...parameters,
    // escrowWaiver is the INVERSE of isImpound. Prefer the stored isImpound and
    // only derive it for older rows saved before that field was persisted.
    isImpound: parameters.isImpound !== undefined ? !!parameters.isImpound : !parameters.escrowWaiver,
  };
  // A search by rate keeps its rate (back on 11-04). The oldest ones kept
  // the rate in targetPrice only; searchTargetOf reads both, and falls back
  // to by price at 100 when there is no usable rate.
  const target = searchTargetOf(parameters);
  if (target.by === "rate") out.targetRate = target.value;
  return out;
}

const purposeLabel = (purpose) => (purpose === "Purchase" ? "Purchase" : purpose === "RateTermRefi" ? "Refinance" : "Cash Out");

const occupancyLabel = (occupancy) => {
  switch (occupancy) {
    case "PRIMARY":
      return "Primary Residence";
    case "SECOND_HOME":
      return "Second Home";
    case "INVESTMENT":
      return "Investment";
    default:
      return occupancy || "";
  }
};

const money = (value) => (Number.isFinite(value) && value > 0 ? `$${Math.round(value).toLocaleString("en-US")}` : "—");

// "6.625%" for a search by rate, "100.000" for one by price, "99.000 cost"
// for a price matched against the Cost to borrower (11-04).
function targetText(parameters) {
  const target = searchTargetOf(parameters);
  if (target.by === "rate") return `${target.value.toFixed(3)}%`;
  const price = Number(target.value).toFixed(3);
  return target.basis === TARGET_BASIS_COST ? `${price} cost` : price;
}

// One row's display values from a stored search.
export function historyRow(item) {
  const p = item.parameters || {};
  const ltv = Number(p.LTV);
  const value = Number(p.propertyValue) || (ltv > 0 ? Number(p.loanAmount) / ltv * 100 : NaN);
  return {
    purpose: purposeLabel(p.purpose),
    occupancy: occupancyLabel(p.occupancy),
    value: money(value),
    fico: p.FICO ? String(p.FICO) : "—",
    ltv: ltv > 0 ? `${ltv.toFixed(2)}%` : "—",
    target: targetText(p),
    targetTitle: searchTargetOf(p).basis === TARGET_BASIS_COST ? "Target price matched against the cost to borrower" : undefined,
    time: item.created_at ? formatTime(item.created_at) : "",
  };
}

// Fetches the saved searches when `active` turns true; newest first, as the
// server returns them.
export function useSearchHistory(active) {
  const [items, setItems] = useState(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    if (!active) return undefined;
    let live = true;
    setFailed(false);
    fetch(HISTORY_URL, {
      method: "GET",
      headers: {
        Authorization: "Bearer " + sessionStorage.getItem("ZeitroA"),
        "Content-Type": "application/json",
      },
    })
      .then((res) => res.json())
      .then((data) => {
        if (!live) return;
        const cutoff = Date.now() - THIRTY_DAYS;
        setItems((Array.isArray(data) ? data : []).filter((item) => item && item.parameters && !(new Date(item.created_at) < cutoff)));
      })
      .catch(() => {
        if (live) {
          setItems([]);
          setFailed(true);
        }
      });
    return () => {
      live = false;
    };
  }, [active]);
  return { items, failed };
}

export function RecentSearchList({ items, onPick, firstRowRef }) {
  return (
    <div className="ppe-recent-list">
      <div className="ppe-recent-row ppe-recent-head" aria-hidden="true">
        <span>Purpose</span>
        <span>Occupancy</span>
        <span className="r">Value</span>
        <span className="r">FICO</span>
        <span className="r">LTV</span>
        <span className="r">Target</span>
        <span className="r">Time</span>
      </div>
      {items.map((item, i) => {
        const r = historyRow(item);
        return (
          <button
            type="button"
            key={item.id || `${item.created_at}-${i}`}
            ref={i === 0 ? firstRowRef : undefined}
            className="ppe-recent-row ppe-recent-item"
            aria-label={`Rerun ${r.purpose}, ${r.occupancy}, value ${r.value}, FICO ${r.fico}, LTV ${r.ltv}, target ${r.target}, ${r.time}`}
            onClick={() => onPick(rerunParameters(item.parameters))}
          >
            <span className="ppe-recent-purpose">{r.purpose}</span>
            <span className="ppe-recent-muted">{r.occupancy}</span>
            <span className="r">{r.value}</span>
            <span className="r">{r.fico}</span>
            <span className="r">{r.ltv}</span>
            <span className="r" title={r.targetTitle}>{r.target}</span>
            <span className="r ppe-recent-time">{r.time}</span>
          </button>
        );
      })}
    </div>
  );
}

// First visit: the list under the search form. Nothing at all when there is
// no history yet.
export function RecentSearchesSection({ onPick }) {
  const { items } = useSearchHistory(true);
  if (!items || items.length === 0) return null;
  return (
    <section className="ppe-recent-section" aria-labelledby="ppe-recent-title">
      <div className="ppe-recent-section-head">
        <span id="ppe-recent-title" className="ppe-recent-title">Recent searches</span>
        <span className="ppe-recent-hint">Pick one to run it again, or fill in the fields and check rates.</span>
      </div>
      <RecentSearchList items={items.slice(0, FIRST_VISIT_ROWS)} onPick={onPick} />
    </section>
  );
}

const clockIcon = (
  <svg width="16" height="16" viewBox="0 0 20 20" fill="none" aria-hidden="true">
    <path
      d="M9.99951 6.66667V10L11.6662 11.6667M2.54102 9.16672C2.72776 7.33344 3.5829 5.63307 4.94329 4.39003C6.30368 3.14699 8.07409 2.44829 9.91675 2.42725C11.7594 2.4062 13.5453 3.06428 14.9337 4.27592C16.3222 5.48756 17.2159 7.16796 17.4445 8.9965C17.6731 10.825 17.2204 12.6737 16.173 14.1899C15.1255 15.706 13.5566 16.7834 11.7654 17.2166C9.97428 17.6498 8.08636 17.4084 6.46185 16.5384C4.83733 15.6685 3.58995 14.231 2.95768 12.5001M2.54102 16.6667V12.5001H6.70768"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// The header's clock icon and the list it opens. Escape or a click outside
// closes it; picking a row closes it and reruns.
export function RecentSearchesButton({ onPick, onOpen }) {
  const [open, setOpen] = useState(false);
  const { items, failed } = useSearchHistory(open);
  const wrapRef = useRef(null);
  const buttonRef = useRef(null);
  const firstRowRef = useRef(null);

  const close = useCallback((refocus) => {
    setOpen(false);
    if (refocus && buttonRef.current) buttonRef.current.focus();
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) close(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") close(true);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, close]);

  useEffect(() => {
    if (open && items && items.length > 0 && firstRowRef.current) firstRowRef.current.focus();
  }, [open, items]);

  let body;
  if (!items) body = <div className="ppe-recent-empty" role="status">Loading…</div>;
  else if (failed) body = <div className="ppe-recent-empty">Recent searches could not be loaded.</div>;
  else if (items.length === 0) body = <div className="ppe-recent-empty">No searches in the last 30 days.</div>;
  else body = <RecentSearchList items={items} firstRowRef={firstRowRef} onPick={(parameters) => { close(false); onPick(parameters); }} />;

  return (
    <div className="ppe-recent-anchor" ref={wrapRef}>
      <button
        type="button"
        ref={buttonRef}
        className="ppe-icon-btn"
        aria-label="Recent searches"
        title="Recent searches"
        aria-expanded={open}
        aria-controls="ppe-recent-popover"
        onClick={() => {
          if (!open && onOpen) onOpen();
          setOpen((v) => !v);
        }}
      >
        {clockIcon}
      </button>
      {open && (
        <div id="ppe-recent-popover" className="ppe-recent-popover" role="dialog" aria-label="Recent searches">
          {body}
        </div>
      )}
    </div>
  );
}
